import { zodResolver } from '@hookform/resolvers/zod';
import { Contactless, DirectionsCar, LocalOffer } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import InputWithChipsComponent from 'components/InputWithChipsComponent';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import ButtonComponent from '../../../../../components/ButtonComponent';
import CheckboxComponent from '../../../../../components/CheckboxComponent';
import DatePickerComponent from '../../../../../components/DatePickerComponent';
import DropdownComponent from '../../../../../components/DropdownComponent';
import DropdownSelectComponent from '../../../../../components/DropdownSelectComponent';
import TextFieldComponent from '../../../../../components/TextFieldComponent';
import { AppContext } from '../../../../../context/AppContextProvider';
import { AddressFormSchema } from '../../../../../services/AddressService/schemas';
import { COUNTRYOPTIONS } from '../../../../../services/AddressService/types';
import { useGetAllDeviceGroupByLicenseAsync } from '../../../../../services/DeviceGroupService/hooks/useMutation';
import { IDeviceGroupSimpleProps } from '../../../../../services/DeviceGroupService/types';
import { useGetAllFacialControllerByLicenseAsync } from '../../../../../services/Devices/Recognition/Controllers/hooks/useMutation';
import { IFacialControllerSimpleProps } from '../../../../../services/Devices/Recognition/Controllers/types';
import {
  useCreateGeneralRecognitionAsync,
  useGetGeneralRecognitionByIdAsync,
  useUpdateGeneralRecognitionAsync,
} from '../../../../../services/Recognition/General/hooks/useMutation';
import { GeneralRecognitionFormSchema } from '../../../../../services/Recognition/General/schema';
import {
  IGeneralRecognitionCreateProps,
  IGeneralRecognitionProps,
  IGeneralRecognitionUpdateProps,
} from '../../../../../services/Recognition/General/types';
import { useGetAllRecognitionGroupByLicenseAsync } from '../../../../../services/Recognition/Group/hooks/useMutations';
import { IRecognitionGroupSimpleProps } from '../../../../../services/Recognition/Group/types';
import { IRecognitionSimpleProps } from '../../../../../services/Recognition/types';
import { useGetAllSchoolClassByLicenseAsync } from '../../../../../services/SchoolClass/hooks/useMutation';
import { ISchoolClassSimpleProps } from '../../../../../services/SchoolClass/types';
import { useGetViaCepAdress } from '../../../../../services/ViaCepService/hooks/useMutation';
import { transformFirebaseLinkToBlob } from '../../../../../utils/TransformUtils';
import { FaceEditionContext } from '../../../context/FaceEditionContextProvider';
import ImageFace from '../cells/ImageFace';

interface IGeneralRecognitionEditFormProps {
  selectedRecognitionId?: number;
  handleSaveForm: () => void;
  selectedRecognitionData?: IRecognitionSimpleProps;
}

type GeneralRecognitionFormInput = z.infer<typeof GeneralRecognitionFormSchema>;
type AddressFormInput = z.infer<typeof AddressFormSchema>;

const GeneralRecognitionEditForm = ({
  handleSaveForm,
  selectedRecognitionId,
  selectedRecognitionData,
}: IGeneralRecognitionEditFormProps) => {
  const methods = useForm<GeneralRecognitionFormInput>({
    resolver: zodResolver(GeneralRecognitionFormSchema),
    defaultValues: {
      name: '',
      cpf: '',
      cnpj: '',
      image: '',
      phone: '',
      vehicleTags: [],
      accessCardTags: [],
      carPlates: [],
      recognitionGroupIds: [],
      facialControllerIds: [],
      deviceGroupIds: [],
      isAddress: false,
      classId: [],
      uc: [],
      type: null,
    },
  });
  const { control, reset, handleSubmit, setValue, watch } = methods;
  const [groups, setGroups] = useState<IRecognitionGroupSimpleProps[]>();
  const [controllers, setControllers] =
    useState<IFacialControllerSimpleProps[]>();
  const [deviceGroups, setDeviceGroups] = useState<IDeviceGroupSimpleProps[]>();

  const isAddress = watch('isAddress');
  const [address, setAddress] = useState<AddressFormInput>();
  const { selectedImage, setSelectedImage, isImageChange } =
    useContext(FaceEditionContext);
  const [shiftOptions, setShiftOptions] = useState<ISchoolClassSimpleProps[]>(
    [],
  );
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const { mutateAsync: mutateGetViaCepAddress } = useGetViaCepAdress();
  const {
    mutateAsync: mutateGetRecognition,
    isLoading: isLoadingGetRecognition,
  } = useGetGeneralRecognitionByIdAsync();
  const {
    mutateAsync: mutateCreateRecognition,
    isLoading: isLoadingCreateRecognition,
  } = useCreateGeneralRecognitionAsync();
  const {
    mutateAsync: mutateUpdateRecognition,
    isLoading: isLoadingUpdateRecognition,
  } = useUpdateGeneralRecognitionAsync();
  const {
    mutateAsync: mutateGetRecognitionGroups,
    isLoading: isLoadingGetRecognitionGroups,
  } = useGetAllRecognitionGroupByLicenseAsync();
  const {
    mutateAsync: mutateGetControllers,
    isLoading: isLoadingGetControllers,
  } = useGetAllFacialControllerByLicenseAsync();
  const { mutateAsync: mutateGetShift, isLoading: isLoadingGetShift } =
    useGetAllSchoolClassByLicenseAsync();
  const {
    mutateAsync: mutateGetDeviceGroups,
    isLoading: isLoadingGetDeviceGroups,
  } = useGetAllDeviceGroupByLicenseAsync();
  const getAddress = async (value?: string) => {
    if (!value) return;

    await mutateGetViaCepAddress(value).then((address) => {
      setValue('address.postalCode', address.postalCode);
      setValue('address.city', address.city);
      setValue('address.state', address.state);
      setValue('address.street', address.street);
      setValue('address.neighborhood', address.neighborhood);
    });
  };
  // const ucOptions = [
  //   { label: 'Oswaldo Cruz', value: 'UNIDADE_1' },
  //   { label: 'Pindorama', value: 'UNIDADE_2' },
  //   { label: 'Fundação Evangélica', value: 'UNIDADE_3' },
  // ];
  const ucOptions = [
    { label: 'Unidade 1', value: 'UNIDADE_1' },
    { label: 'Unidade 2', value: 'UNIDADE_2' },
    { label: 'Unidade 3', value: 'UNIDADE_3' },
  ];
  const normalizeFormData = async (data: GeneralRecognitionFormInput) => {
    if (data.validUntil) {
      data.validUntil = data.validUntil.trim().split('T')[0];
    }

    if (data.cnpj) {
      data.cnpj = data.cnpj.trim().replace(/\D/g, '');
    }

    data.cpf = data.cpf.trim().replace(/\D/g, '');
    data.phone = data.phone.trim().replace(/\D/g, '');

    if (isImageChange) data.image = '';
  };

  const handleSave = async (data: GeneralRecognitionFormInput) => {
    setIsShowLoadingComponent(false);
    if (!selectedImage) {
      showSnackbar(
        t('recognitionPage.registerFace.youForgotToTakeAPicture'),
        'error',
      );
      return;
    } else {
      await normalizeFormData(data);
    }

    const isEdit = !!data.id;
    if (isEdit) {
      showSnackbar(t('recognitionPage.registerFace.updatingFace'), 'info');
    } else {
      showSnackbar(t('recognitionPage.registerFace.registeringFace'), 'info');
    }

    const formattedData = {
      ...data,
      uc: Array.isArray(data.uc) ? data.uc : data.uc ? [data.uc] : [],
    };

    try {
      if (data.id) {
        const general: IGeneralRecognitionUpdateProps = { ...data, carPlates: data.carPlates ?? undefined };
        general.file = selectedImage as Blob;
        await mutateUpdateRecognition({
          id: data.id,
          data: general,
        });
        resetAllAndShowMessage(t('recognitionPage.registerFace.updatedFace'));
      } else {
        const general: IGeneralRecognitionCreateProps = { ...data, carPlates: data.carPlates ?? undefined };
        general.file = selectedImage as Blob;

        await mutateCreateRecognition(general);
        resetAllAndShowMessage(t('recognitionPage.registerFace.registerFace'));
      }
    } catch (error: any) {
      console.error('Erro na requisição:', error);
      setIsShowLoadingComponent(false);

      const errorMessage = error?.response?.data?.Message || error?.message || t('common.errorSaving');
      showSnackbar(errorMessage, 'error');
    }
  };

  const resetAllAndShowMessage = (message: string) => {
    setSelectedImage('');
    reset();
    if (handleSaveForm) handleSaveForm();
    setIsShowLoadingComponent(false);
    showSnackbar(message, 'success');
  };

  const mapRecognitionToForm = (
    response: IGeneralRecognitionProps,
  ): GeneralRecognitionFormInput => {
    const validUntilStr =
      response.validUntil != null
        ? dayjs(response.validUntil).toISOString()
        : null;

    const hasAddress = !!response.address;

    const addressDefaults = {
      id: undefined,
      postalCode: '',
      country: '',
      state: '',
      city: '',
      neighborhood: '',
      street: '',
      addrNumber: '',
      complement: '',
    };

    if (hasAddress) {
      setAddress(response.address);
      return {
        id: response.id,
        name: response.name,
        cpf: response.cpf ?? '',
        cnpj: response.cnpj ?? '',
        image: response.image ?? '',
        recognitionGroupIds: response.recognitionGroups?.map((i) => i.id) ?? [],
        facialControllerIds: response.facialControllers?.map((i) => i.id) ?? [],
        uc: response.uc && response.uc.length > 0 ? response.uc : [],
        validUntil: validUntilStr,
        vehicleTags: response.vehicleTags ?? [],
        accessCardTags: response.accessCardTags ?? [],
        carPlates: response.carPlates ?? [],
        phone: response.phone,
        isAddress: true,
        address: { ...addressDefaults, ...response.address! },
        file: '',
      };
    } else {
      return {
        id: response.id,
        name: response.name,
        cpf: response.cpf ?? '',
        cnpj: response.cnpj ?? '',
        image: response.image ?? '',
        recognitionGroupIds: response.recognitionGroups?.map((i) => i.id) ?? [],
        facialControllerIds: response.facialControllers?.map((i) => i.id) ?? [],
        deviceGroupIds: response.deviceGroups?.map((i) => i.id) ?? [],
        validUntil: validUntilStr,
        vehicleTags: response.vehicleTags ?? [],
        accessCardTags: response.accessCardTags ?? [],
        carPlates: response.carPlates ?? [],
        phone: response.phone,
        isAddress: false,
        address: null,
        type: response.type ?? null,
        uc: response.uc && response.uc.length > 0 ? response.uc : [],
        classId: response.classId ?? [],
        file: '',
      };
    }
  };

  useEffect(() => {
    if (!isAddress) {
      setValue('address', null);
    } else {
      if (address) {
        setValue('address', address);
      } else {
        setValue('address', {
          country: '',
          state: '',
          city: '',
          neighborhood: '',
          street: '',
          postalCode: '',
          name: '',
          addrNumber: '',
          complement: '',
        });
      }
    }
  }, [isAddress, address]);

  useEffect(() => {
    if (!controllers) {
      mutateGetControllers({
        pagination: {
          pageSize: 30,
          pageNumber: 1,
        },
      }).then(setControllers);
    }
  }, [controllers]);
  useEffect(() => {
    mutateGetShift().then(setShiftOptions);
  }, []);
  useEffect(() => {
    if (!deviceGroups) {
      mutateGetDeviceGroups().then(setDeviceGroups);
    }
  }, [deviceGroups]);
  useEffect(() => {
    if (!selectedRecognitionData) return;

    if (selectedRecognitionData?.image) {
      setSelectedImage(selectedRecognitionData.image);
      setValue('name', selectedRecognitionData.name);
    }

    if (selectedRecognitionData.cpf)
      setValue('cpf', selectedRecognitionData.cpf);

    if (selectedRecognitionData.facialControllersIds) {
      setValue(
        'facialControllerIds',
        selectedRecognitionData.facialControllersIds,
      );
    }
  }, [selectedRecognitionData]);

  useEffect(() => {
    if (!groups) {
      mutateGetRecognitionGroups({}).then(setGroups);
    }
  }, [groups]);

  useEffect(() => {
    if (selectedRecognitionId) {
      mutateGetRecognition(selectedRecognitionId)
        .then(async (response) => {
          const formValues = mapRecognitionToForm(response);
          reset(formValues, { keepDefaultValues: true });
          try {
            if (response.image) {
              const image = await transformFirebaseLinkToBlob(response.image);
              setSelectedImage(image);
            }
          } catch (error) {
            console.error('Erro ao carregar a imagem:', error);
            showSnackbar(
              'Não foi possível carregar a imagem do servidor.',
              'warning',
            );
          }
        })
        .finally(() => setIsShowLoadingComponent(false));
    }
  }, [selectedRecognitionId]);

  useEffect(() => {
    return () => {
      setSelectedImage('');
    };
  }, []);

  return (
    <>
      <Grid2
        container
        direction={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        size={12}
        spacing={3}
      >
        <Grid2
          display={'flex'}
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={'center'}
          alignItems={{ xs: 'center', md: 'normal' }}
          size={12}
          gap={{ xs: 0, md: 3 }}
        >
          <Grid2 size={{ xs: 12, lg: 6 }}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={`${t('recognitionPage.modalEditFace.name')} *`}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'name'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={`${t('recognitionPage.modalEditFace.cpf')} *`}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  mask={'000.000.000-00'}
                />
              )}
              name={'cpf'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.cnpj')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  isOptional={true}
                  mask={'00.000.000/0000-00'}
                />
              )}
              name={'cnpj'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DatePickerComponent
                  value={value ? dayjs(value).utc() : null}
                  onChange={(value) => {
                    const date = dayjs(value);
                    onChange(
                      date.isValid() ? dayjs(value).toISOString() : null,
                    );
                  }}
                  errorMessage={error?.message}
                  label={t('recognitionPage.modalEditFace.faceValidity')}
                  isOptional={true}
                  disablePast={true}
                />
              )}
              name={'validUntil'}
              control={control}
            />

            <Controller
              name="vehicleTags"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <InputWithChipsComponent
                  label={t('recognitionPage.modalEditFace.vehicleTags')}
                  tags={value || []}
                  onChangeTags={onChange}
                  chipIcon={<LocalOffer />}
                  errorMessage={error?.message}
                  formatInput={(val) => val.trim().replace(/[^A-Z0-9]/gi, '').toUpperCase()} 
                  maxLength={24}
                />
              )}
            />

            <Controller
              name="accessCardTags"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <InputWithChipsComponent
                  label={t('recognitionPage.modalEditFace.accessCardTags')}
                  tags={value || []}
                  onChangeTags={onChange}
                  chipIcon={<Contactless />}
                  errorMessage={error?.message}
                  formatInput={(val) => val.trim().replace(/[^A-Z0-9]/gi, '').toUpperCase()} 
                  maxLength={24}
                />
              )}
            />

            <Controller
              name="carPlates"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <InputWithChipsComponent
                  label={t('recognitionPage.modalEditFace.carPlates')}
                  tags={value || []}
                  onChangeTags={onChange}
                  chipIcon={<DirectionsCar />}
                  errorMessage={error?.message}
                  formatInput={(val) => val.trim().replace(/[^A-Z0-9]/gi, '').toUpperCase()} 
                  maxLength={7}
                />
              )}
            />

            <Controller
              name="type"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormLabel component="legend">
                    {t('recognitionPage.modalEditFace.type')}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    aria-label={t('recognitionPage.modalEditFace.type')}
                    name="type-radio-group"
                  >
                    <FormControlLabel
                      value="FUNCIONARIO"
                      control={<Radio />}
                      label={t('recognitionPage.modalEditFace.employee')}
                    />
                    <FormControlLabel
                      value="ASSOCIADO"
                      control={<Radio />}
                      label="ASSOCIADO"
                    />
                    <FormControlLabel
                      value="PRESTADOR"
                      control={<Radio />}
                      label={t('recognitionPage.modalEditFace.serviceProvider')}
                    />
                    <FormControlLabel
                      value="VISITANTE"
                      control={<Radio />}
                      label={t('recognitionPage.modalEditFace.visitor')}
                    />
                    <FormControlLabel
                      value="PROFESSOR"
                      control={<Radio />}
                      label="Professor"
                    />
                  </RadioGroup>
                  {error?.message && (
                    <Typography variant="caption" color="error">
                      {error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownSelectComponent
                  label={t('recognitionPage.modalEditFace.faceGroup')}
                  options={groups}
                  labelField={'name'}
                  valueField={'id'}
                  errorMessage={error?.message}
                  selectedValues={value}
                  onSelectedValues={onChange}
                />
              )}
              name={'recognitionGroupIds'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownSelectComponent
                  label={t('recognitionPage.modalEditFace.facialController')}
                  options={controllers}
                  labelField={'name'}
                  valueField={'id'}
                  errorMessage={error?.message}
                  selectedValues={value}
                  onSelectedValues={onChange}
                />
              )}
              name={'facialControllerIds'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownSelectComponent
                  label={'Grupo de dispositivos'}
                  options={deviceGroups}
                  labelField={'name'}
                  valueField={'id'}
                  errorMessage={error?.message}
                  selectedValues={value}
                  onSelectedValues={onChange}
                />
              )}
              name={'deviceGroupIds'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error, invalid },
              }) => (
                <DropdownSelectComponent
                  options={shiftOptions}
                  label={t('recognitionPage.modalEditFace.shift')}
                  labelField={'name'}
                  valueField={'id'}
                  error={invalid}
                  errorMessage={error?.message}
                  selectedValues={value as any[]}
                  onSelectedValues={onChange}
                />
              )}
              name={'classId'}
              control={control}
            />
            <Controller
              name="uc"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownSelectComponent
                  label={t('recognitionPage.modalEditFace.corporateUnity')}
                  options={ucOptions}
                  labelField="label"
                  valueField="value"
                  errorMessage={error?.message}
                  selectedValues={value || []}
                  onSelectedValues={onChange}
                />
              )}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={`${t('recognitionPage.modalEditFace.cellPhone')} *`}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  mask={'(00) 0 0000-0000'}
                  maxLength={16}
                />
              )}
              name={'phone'}
              control={control}
            />
            <Grid2
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              mt={4}
            >
              <Controller
                name="isAddress"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CheckboxComponent
                    label={t('recognitionPage.modalEditFace.addAddress')}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Grid2>
        {isAddress && (
          <>
            <Grid2 display={'flex'} justifyContent={'start'} width={'100%'}>
              <Typography variant={'h5'} component={'h2'} fontWeight={'bolder'}>
                {t('recognitionPage.modalEditFace.address.address')}
              </Typography>
            </Grid2>
            <Grid2
              display={'flex'}
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent={'center'}
              alignItems={{ xs: 'center', md: 'normal' }}
              size={12}
              gap={{ xs: 0, md: 3 }}
            >
              <Grid2 size={{ xs: 12, lg: 6 }}>
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.postalCode',
                      )}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      onBlur={() => getAddress(value)}
                      errorMessage={error?.message}
                      value={value}
                      mask={'00000-000'}
                    />
                  )}
                  name={'address.postalCode'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <DropdownComponent
                      label={t('recognitionPage.modalEditFace.address.country')}
                      options={COUNTRYOPTIONS}
                      optionLabel={'label'}
                      optionValue={'value'}
                      errorMessage={error?.message}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                  name={'address.country'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t('recognitionPage.modalEditFace.address.state')}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.state'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t('recognitionPage.modalEditFace.address.city')}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.city'}
                  control={control}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, lg: 6 }}
                display={'column'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.neighborhood',
                      )}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.neighborhood'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t('recognitionPage.modalEditFace.address.street')}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.street'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.addrNumber',
                      )}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      isOptional={true}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.addrNumber'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.complement',
                      )}
                      isOptional={true}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.complement'}
                  control={control}
                />
              </Grid2>
            </Grid2>
          </>
        )}
        <Grid2
          size={8}
          display={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          mb={3}
        >
          <ImageFace handleCancelImageEdition={() => setSelectedImage('')} />
        </Grid2>
      </Grid2>
      <ButtonComponent
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSubmit(handleSave)}
      >
        {t('recognitionPage.modalEditFace.buttonCaptureImage.save')}
      </ButtonComponent>
    </>
  );
};

export default GeneralRecognitionEditForm;
